import { Component, Input, OnInit } from '@angular/core';
import { AssetType } from '../../../models/asset/assetType';
import { FabricCategory } from '../../../models/fabric/fabricCategory';
import { ModalService } from '@card-software/library';
import { ApiResponse, AuthService } from '@card-software/nexus-utilities';
import { FabricCategoryProxyService } from '../../../proxies/item/fabric-category.proxy.service';
import { AssetTypeProxyService } from '../../../proxies/asset/asset-type.proxy.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TableRowExpandEvent } from 'primeng/table';
import { CategoriesModalComponent } from '../../modals/categories-modal/categories-modal.component';
import { CompositionProxyService } from '../../../proxies/item/composition-proxy.service';
import { Composition } from '../../../models/composition/composition';
import { Observable } from 'rxjs';
import { ItemCategoryProxyService } from '../../../proxies/item/item-category.proxy.service';
import { Translation } from '../../../models/Translations';
import { TranslateService } from '@ngx-translate/core';
import {
  DataMasterAdminGroup,
  DataMasterViewerGroup,
  ProductDevelopmentAdminGroup,
  ProductDevelopmentViewerGroup,
} from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-categories-table',
  templateUrl: './categories-table.component.html',
  styleUrl: './categories-table.component.scss',
})
export class CategoriesTableComponent implements OnInit {
  @Input() categoryType: 'Fabric' | 'Fiber' | 'Item' = 'Fabric';
  @Input() levels = 3;
  allCategories: FabricCategory[] | Composition[] = [];
  categories: FabricCategory[] | Composition[] = [];
  subcategories: FabricCategory[] | Composition[] = [];
  isLoading = false;
  machineTypes: AssetType[] = [];
  // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
  expandedRows: { [key: string]: boolean } = {};
  translations: Record<string, Translation> = {
    Category_Type_Fabric: {
      es: 'Tela',
      en: 'Fabric',
    },
    Category_Type_Fiber: {
      es: 'Fibra',
      en: 'Fiber',
    },
    Category_Type_Item: {
      es: 'Artículo',
      en: 'Item',
    },
    Categories: {
      es: 'Categorías de ',
      en: 'Categories',
    },
    Categories_Table_Delete_Category: {
      es: '¿Desea eliminar esta categoría?',
      en: 'Do you want to delete this category?',
    },
    Categories_Table_Danger_Zone: {
      es: 'Zona de Peligro',
      en: 'Danger Zone',
    },
    Categories_Table_Cancel: {
      es: 'Cancelar',
      en: 'Cancel',
    },
    Categories_Table_Delete: {
      es: 'Eliminar',
      en: 'Delete',
    },
    Categories_Table_Success_Deleting_Category: {
      es: `Categoría '{{name}}' eliminada correctamente`,
      en: `Category '{{name}}' deleted successfully`,
    },
  };

  constructor(
    private modalService: ModalService,
    private fabricCategoryProxyService: FabricCategoryProxyService,
    private compositionProxyService: CompositionProxyService,
    private itemCategoryProxyService: ItemCategoryProxyService,
    private assetTypeProxyService: AssetTypeProxyService,
    private readonly messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translateService: TranslateService,
  ) {}

  canEdit() {
    // if Admin
    if (
      (this.categoryType === 'Fabric' && AuthService.hasRole(ProductDevelopmentAdminGroup.FabricCategoriesAdmin)) ||
      (this.categoryType === 'Fiber' && AuthService.hasRole(ProductDevelopmentAdminGroup.FiberCategoriesAdmin)) ||
      (this.categoryType === 'Item' && AuthService.hasRole(DataMasterAdminGroup.ItemCategoriesAdmin))
    ) {
      return true;
    }
    // if Viewer
    if (
      (this.categoryType === 'Item' && AuthService.hasRole(DataMasterViewerGroup.ItemCategoriesViewer)) ||
      (this.categoryType === 'Fabric' && AuthService.hasRole(ProductDevelopmentViewerGroup.FabricCategoriesViewer)) ||
      (this.categoryType === 'Fiber' && AuthService.hasRole(ProductDevelopmentViewerGroup.FiberCategoriesViewer))
    ) {
      return false;
    }
    // Default
    return false;
  }

  getCategoryTypeTranslation(): string {
    const categoryType = this.translations[`Category_Type_${this.categoryType}`][this.translateService.currentLang as keyof Translation];
    const categories = this.translations['Categories'][this.translateService.currentLang as keyof Translation];
    if (this.translateService.currentLang === 'es') {
      return `${categories} ${categoryType}`;
    }
    return `${categoryType} ${categories}`;
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    if (this.categoryType === 'Fabric') {
      this.getMachineTypes();
      this.getFabricCategories();
    } else if (this.categoryType === 'Fiber') {
      this.getFiberCategories();
    } else {
      this.getItemCategories();
    }
  }

  getMachineTypes() {
    this.assetTypeProxyService.getByCategoryId('673c07b2a128eab872eb6c2d').subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.machineTypes = response.data;
        }
      },
      error: (error) => {
        console.log('🚀 ~ this.assetTypeProxyService.getByCategoryId ~ error:', error);
      },
    });
  }

  getMachineTypeById(machineTypeReference: string): string {
    return this.machineTypes.find((machine) => machine.id === machineTypeReference)?.name || '';
  }

  getFiberCategories() {
    this.isLoading = true;

    this.compositionProxyService.getAllCompositions().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success && response.data) {
          this.allCategories = response.data;
          this.getAllParentCategories();
          this.getAllSubcategories();
        }
      },
      error: (error) => {
        console.log('🚀 ~ CategoriesTableComponent ~ this.compositionProxyService.getAllCompositions ~ error:', error);
        this.isLoading = false;
      },
    });
  }

  getFabricCategories() {
    this.isLoading = true;

    this.fabricCategoryProxyService.getFabricCategories().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success && response.data) {
          this.allCategories = response.data;
          this.getAllParentCategories();
          this.getAllSubcategories();
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.log('🚀 ~ FabricCategoriesComponent ~ this.fabricCategoryProxyService.getFabricCategories ~ error:', error);
      },
    });
  }

  getItemCategories() {
    this.isLoading = true;

    this.itemCategoryProxyService.getAllItemsCategories().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success && response.data) {
          this.allCategories = response.data;
          this.getAllParentCategories();
          this.getAllSubcategories();
        }
      },
      error: (error) => {
        console.log('🚀 ~ CategoriesTableComponent ~ this.itemCategoryProxyService.getAllItemsCategories ~ error:', error);
        this.isLoading = false;
      },
    });
  }

  getAllParentCategories() {
    this.categories = this.allCategories.filter(
      (category) => (!category.parentCode || category.level == 1) && category.isActive, // only filter by categories without parentCode
    );
  }

  getAllSubcategories() {
    this.subcategories = this.allCategories.filter((subcategory) => subcategory.parentCode && subcategory.isActive);
  }

  getSubcategoriesByParentCode(parentCode: string) {
    return this.subcategories.filter((subcategory) => subcategory.parentCode === parentCode);
  }

  onCategoryExpand(expandedCategory: TableRowExpandEvent) {
    const selectedCategory: FabricCategory | Composition = expandedCategory.data;
    let selectedCategoryIndex = -1;

    if (selectedCategory.level == 1) {
      selectedCategoryIndex = this.categories.findIndex((category) => category.id === selectedCategory.id);

      this.categories[selectedCategoryIndex].subcategories = this.getSubcategoriesByParentCode(selectedCategory.code!);
    }

    if (selectedCategory.level == 2) {
      selectedCategoryIndex = this.subcategories.findIndex((subcategory) => subcategory.id === selectedCategory.id);

      this.subcategories[selectedCategoryIndex].subcategories = this.getSubcategoriesByParentCode(selectedCategory.code!);
    }
  }

  openCategoriesModal(category?: FabricCategory | Composition, addSubcategories = false) {
    const createCategory = category ? false : true;

    this.modalService
      .open(
        CategoriesModalComponent,
        { modalClass: 'modal_small' },
        {
          categoryType: this.categoryType,
          category: structuredClone(category),
          createCategory,
          addSubcategories,
          canEdit: this.canEdit(),
        },
      )
      .subscribe((result) => {
        if (result && result.action === 'save' && result.data) {
          const updatedCategory = result.data as FabricCategory;
          const index = this.allCategories.findIndex((c) => c.id === updatedCategory.id);

          if (index !== -1) {
            this.allCategories[index] = {
              ...this.allCategories[index],
              ...updatedCategory,
            }; // Replace the existing item
          } else {
            this.allCategories.push(updatedCategory); // Add the item if it doesn't exist
          }
          // refresh categories
          this.getAllParentCategories();
          this.getAllSubcategories();
          // Expand category table row
          if (updatedCategory?.parentCode) {
            const parentCategory = this.allCategories.find((c) => c.code === updatedCategory.parentCode);
            this.expandedRows[parentCategory!.id!] = true;
            this.onCategoryExpand({
              data: parentCategory,
            } as TableRowExpandEvent);
          }
        }

        if (result && result.action === 'save_subcategories' && result.data) {
          const subcategories = result.data as FabricCategory[] | Composition[];
          if (subcategories.length > 0) {
            this.allCategories = [...this.allCategories, ...subcategories];
            // refresh categories
            this.getAllParentCategories();
            this.getAllSubcategories();
          }
          // Expand category table row
          if (category?.id) {
            this.expandedRows[category.id] = true;
            this.onCategoryExpand({ data: category } as TableRowExpandEvent);
          }
        }
      });
  }

  deletePopup(event: Event, category: FabricCategory | Composition) {
    event.stopPropagation();
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['Categories_Table_Delete_Category'][this.translateService.currentLang as keyof Translation],
      header: this.translations['Categories_Table_Danger_Zone'][this.translateService.currentLang as keyof Translation],
      icon: 'pi pi-info-circle',
      rejectLabel: this.translations['Categories_Table_Cancel'][this.translateService.currentLang as keyof Translation],
      rejectButtonProps: {
        label: this.translations['Categories_Table_Cancel'][this.translateService.currentLang as keyof Translation],
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translations['Categories_Table_Delete'][this.translateService.currentLang as keyof Translation],
        severity: 'danger',
      },

      accept: () => {
        this.deleteCategory(category);
      },
    });
  }

  deleteCategory(category: FabricCategory | Composition) {
    let delete$: Observable<ApiResponse<void>>;

    if (this.categoryType === 'Fabric') {
      delete$ = this.fabricCategoryProxyService.deleteFabricCategoryById(category.id!);
    } else if (this.categoryType === 'Fiber') {
      delete$ = this.compositionProxyService.deleteFiberCategoryById(category.id!);
    } else {
      delete$ = this.itemCategoryProxyService.deleteFiberItemById(category.id!);
    }

    delete$.subscribe({
      next: (response) => {
        if (response.success) {
          const index = this.allCategories.findIndex((c) => c.id === category.id);
          if (index !== -1) {
            this.allCategories.splice(index, 1);
          }
          // refresh categories
          this.getAllParentCategories();
          this.getAllSubcategories();
          // remove subcategory from table.
          const categoryLevel = category.level || 0;
          if (categoryLevel > 1) {
            const parentCategory = this.allCategories.find((c) => c.code === category.parentCode);

            this.onCategoryExpand({
              data: parentCategory,
            } as TableRowExpandEvent);
          }

          this.messageService.add({
            severity: 'success',
            summary: this.translations['Categories_Table_Success_Deleting_Category'][
              this.translateService.currentLang as keyof Translation
            ].replace('{{name}}', category?.code ?? ''),
            key: 'br',
            life: 2500,
          });
        }
      },
      error: (error) => {
        console.error('❌ Error deleting category:', error);
      },
    });
  }
}
